import { ChallengeService } from '@duck/api-definitions'
import { WysiwygEditor } from '@components/WysiwygEditor'
import { useAtomValue, useSetAtom } from 'jotai'
import { WorkspaceHeaderPane } from './WorkspaceHeaderPane'
import { currentSolutionAtom } from '@/store/saveWorkAtoms'
import { useTar } from '@/modules/i18n'
import { MarkdownEditor, Button } from '@duck/ui'
import { useState, useCallback, lazy, Suspense } from 'react'
import { SqlTab } from '../WorkspaceTabs/WorkspaceTabs/Sql/Sql.tab'
import { apis } from '@modules/apiClients'
import { useUserBadges } from '@/hooks/useUserBadges'
import { UserService } from '@duck/api-definitions'
import { Laptop } from 'lucide-react'

const LazyPythonNotebookV2 = lazy(() =>
    import('../PythonNotebookV2').then(m => ({ default: m.PythonNotebookV2 }))
)

export interface MobileWorkspaceProps {
    challenge: ChallengeService.ChallengeInfo['challenge']
    task: ChallengeService.ChallengeInfo['task']
    charts: ChallengeService.WorkState['charts']
    challengeId: string
    workTime: number
    handleSolutionChange: (solution: string) => void
    handleChartAdded: (solution: string, chart: ChallengeService.Chart) => void
    handleChartRemoved: (solution: string, remainingChartIds: string[]) => void
}

export const MobileWorkspace = ({
    challenge,
    task,
    charts,
    challengeId,
    workTime,
    handleSolutionChange,
    handleChartAdded,
    handleChartRemoved,
}: MobileWorkspaceProps) => {
    const currentSolution = useAtomValue(currentSolutionAtom)
    const setCurrentSolution = useSetAtom(currentSolutionAtom)
    const { t } = useTar()
    const { hasBadge } = useUserBadges()
    const isPython = challenge.workState?.chosenWeapon === ChallengeService.TaskWeaponsOfChoiceEnum.Python
    const pythonOnMobile = isPython && hasBadge(UserService.UserBadge.MobilePython)
    const [activeTab, setActiveTab] = useState<'assignment' | 'query' | 'solution'>('assignment')

    const handleQueryUpdate = useCallback(
        (query: string) => {
            if (!challengeId) return
            void apis.challengeService.saveWork({
                challengeId,
                workState: { lastQuery: query },
            })
        },
        [challengeId]
    )


    return (
        <div className="flex flex-col flex-1 min-w-0 relative">
            <div className="px-4">
                <WorkspaceHeaderPane
                    challenge={challenge}
                    task={task}
                    workTime={workTime}
                    showSubmitButton={!challenge.isSandbox}
                />
            </div>
            <div className="flex gap-2 p-4 border-b border-border w-full">
                <Button
                    onClick={() => setActiveTab('assignment')}
                    variant={activeTab === 'assignment' ? 'default' : 'outline'}
                    className="flex-1"
                >
                    {t('common.assignment')}
                </Button>
                <Button
                    onClick={() => setActiveTab('query')}
                    variant={activeTab === 'query' ? 'default' : 'outline'}
                    className="flex-1"
                >
                    {isPython ? t('common.settingsLanguagePython') : t('common.data')}
                </Button>
                {/* The notebook is the solution for Python; the WYSIWYG solution is SQL-only, as on desktop. */}
                {!isPython && (
                    <Button
                        onClick={() => setActiveTab('solution')}
                        variant={activeTab === 'solution' ? 'default' : 'outline'}
                        className="flex-1"
                    >
                        {t('common.solution')}
                    </Button>
                )}
            </div>
            <div className="flex-1 overflow-y-auto">
                {activeTab === 'assignment' && (
                    <div className="prose prose-slate dark:prose-invert max-w-none overflow-y-auto custom-scrollbar">
                        <MarkdownEditor
                            defaultValue={task.body}
                            mdClassName="border-none"
                            mode="view"
                        />
                    </div>
                )}
                {activeTab === 'query' && isPython && !pythonOnMobile && (
                    <div className="flex flex-col items-center gap-3 p-8 text-center text-muted-foreground">
                        <Laptop className="h-10 w-10" aria-hidden="true" />
                        <p className="font-medium text-foreground">{t('common.pythonNotOnMobileTitle')}</p>
                        <p className="text-sm">{t('common.pythonNotOnMobileBody')}</p>
                    </div>
                )}
                {activeTab === 'query' && pythonOnMobile && challengeId && (
                    <div className="h-full min-h-[400px]">
                        <Suspense fallback={null}>
                            <LazyPythonNotebookV2 />
                        </Suspense>
                    </div>
                )}
                {activeTab === 'query' && !isPython && challengeId && (
                    <div className="h-full min-h-[400px]">
                        <SqlTab
                            query={challenge.workState?.lastQuery ?? task.defaultQuery ?? ''}
                            onUpdateData={handleQueryUpdate}
                            isVisible={activeTab === 'query'}
                        />
                    </div>
                )}
                {activeTab === 'solution' && charts && (
                    <div className="max-w-[100dvw]">
                        <WysiwygEditor
                            telemetrySurface="solution"
                            content={currentSolution}
                            onChange={(solution) => {
                                handleSolutionChange(solution)
                                setCurrentSolution(solution)
                            }}
                            handleChartAdded={handleChartAdded}
                            handleChartRemoved={handleChartRemoved}
                            startingCharts={charts}
                            placeholder={t('common.solveHere')}
                            className='p-0'
                        />
                    </div>
                )}
            </div>
        </div>
    )
}
